import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useMembershipPlanService, { MembershipPlan } from '../../app/services/hooks/useMembershipPlanService';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import ButtonTableAction from '../../components/buttons/ButtonTableAction';
import InputField from '../../components/InputField';

const MembershipPlanList: React.FC = () => {
    const history = useHistory();
    const { requestConfirmation } = useReactConfirmAlert();
    const {
        getMembershipPlans,
        deleteMembershipPlan,
        activeMembershipPlan,
        fetchingGetPlans,
        fetchingDeletePlan,
        fetchingActivePlan
    } = useMembershipPlanService();
    
    const [plans, setPlans] = useState<MembershipPlan[]>([]);
    const [filteredPlans, setFilteredPlans] = useState<MembershipPlan[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    useEffect(() => {
        loadPlans();
    }, []);
    
    useEffect(() => {
        filterData();
    }, [searchTerm, plans]);
    
    const loadPlans = () => {
        getMembershipPlans({}, {
            onSuccess: (res: any) => {
                setPlans(res.data.plans || []);
            },
            onError: (res: any) => {
                toast.error(res.message || 'Error al cargar planes de membresía');
            }
        });
    };
    
    const filterData = () => {
        let filtered = [...plans];
        
        if (searchTerm) {
            filtered = filtered.filter(plan =>
                plan.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                plan.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                plan.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        setFilteredPlans(filtered);
    };
    
    const handleEdit = (id: number) => {
        history.push(`/intranet/membership-plans/${id}/edit`);
    };

    const handleCreate = () => {
        history.push('/intranet/membership-plans/create');
    };

    const handleDelete = (plan: MembershipPlan) => {
        requestConfirmation({
            title: '¿Eliminar plan?',
            message: `¿Estás seguro de eliminar el plan "${plan.name}"? Esta acción no se puede deshacer.`,
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        deleteMembershipPlan(plan.id, {
                            onSuccess: (res: any) => {
                                toast.success(res.message || 'Plan eliminado correctamente');
                                loadPlans();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || 'Error al eliminar el plan');
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const handleToggleActive = (plan: MembershipPlan) => {
        const newStatus = !plan.is_active;
        const actionText = newStatus ? 'activar' : 'desactivar';
        
        requestConfirmation({
            title: `¿${newStatus ? 'Activar' : 'Desactivar'} plan?`,
            message: `¿Estás seguro de ${actionText} el plan "${plan.name}"?`,
            buttons: {
                confirmButton: {
                    label: `Sí, ${actionText}`,
                    onClick: () => {
                        activeMembershipPlan(plan.id, newStatus, {
                            onSuccess: (res: any) => {
                                toast.success(res.message || `Plan ${newStatus ? 'activado' : 'desactivado'} correctamente`);
                                loadPlans();
                            },
                            onError: (res: any) => {
                                toast.error(res.message || `Error al ${actionText} el plan`);
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'Cancelar'
                }
            }
        });
    };

    const formatPrice = (price: number) => {
        return `$${price.toLocaleString('es-CL')}`;
    };
    
    if (fetchingGetPlans) {
        return (
            <div className="container-fluid">
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', path: '#' },
                    { label: 'Planes de Membresía', isCurrent: true }
                ]}
                title="Planes de Membresía"
                description="Gestión de planes de suscripción para acceso a contratos y chatbot"
            />
            
            <DefaultCard>
                {/* Filtros y Búsqueda */}
                <div className="row mb-3 align-items-end">
                    <div className="col-md-6">
                        <InputField
                            label="Buscar"
                            value={searchTerm}
                            onChange={(value) => setSearchTerm(value)}
                            placeholder="Buscar por nombre, código o descripción..."
                            icon="fa-search"
                        />
                    </div>
                    <div className="col-md-6 d-flex justify-content-end">
                        <ButtonGeneral
                            onClick={handleCreate}
                            title="Nuevo Plan"
                            variant="primary"
                            size="small"
                            icon="fa-plus"
                            iconPosition="left"
                        />
                    </div>
                </div>
                
                {/* Tabla */}
                <div className="table-responsive">
                    <table className="table table-hover">
                        <thead>
                            <tr>
                                <th>Plan</th>
                                <th>Precio</th>
                                <th>Duración</th>
                                <th>Contratos</th>
                                <th>Chatbot</th>
                                <th>Estado</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPlans.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">
                                        {searchTerm
                                            ? 'No se encontraron planes con los filtros aplicados'
                                            : 'No hay planes de membresía registrados'}
                                    </td>
                                </tr>
                            ) : (
                                filteredPlans.map((plan) => (
                                    <tr 
                                        key={plan.id}
                                        style={{ opacity: plan.is_active ? 1 : 0.6 }}
                                    >
                                        <td>
                                            <div>
                                                <span className="fw-bold" style={{ color: '#001849' }}>
                                                    {plan.name}
                                                </span>
                                                {plan.is_featured && (
                                                    <span className="badge badge-soft-primary ms-2" style={{ fontSize: '0.65rem' }}>
                                                        Destacado
                                                    </span>
                                                )}
                                                {plan.badge_text && (
                                                    <span className="badge badge-soft-dark ms-2" style={{ fontSize: '0.65rem' }}>
                                                        {plan.badge_text}
                                                    </span>
                                                )}
                                            </div>
                                            <small className="text-muted">{plan.code}</small>
                                        </td>
                                        <td>
                                            <span className="fw-bold">
                                                {formatPrice(plan.price)}
                                            </span>
                                            <span className="text-muted ms-1" style={{ fontSize: '0.8rem' }}>
                                                + IVA
                                            </span>
                                        </td>
                                        <td>
                                            <span className="badge bg-secondary">
                                                {plan.duration_display || `${plan.duration_days} días`}
                                            </span>
                                        </td>
                                        <td>
                                            {plan.contracts_included === -1 ? (
                                                <span className="text-success fw-bold">
                                                    <i className="fas fa-infinity me-1"></i>
                                                    Ilimitados
                                                </span>
                                            ) : (
                                                <span>{plan.contracts_included}</span>
                                            )}
                                        </td>
                                        <td>
                                            {plan.chatbot_enabled ? (
                                                <span className="text-success">
                                                    <i className="fas fa-check-circle me-1"></i>
                                                    {plan.chatbot_messages_limit === -1 ? 'Ilimitado' : `${plan.chatbot_messages_limit} msgs`}
                                                </span>
                                            ) : (
                                                <span className="text-muted">
                                                    <i className="fas fa-times-circle me-1"></i>
                                                    No incluido
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span 
                                                className={`badge ${plan.is_active ? 'badge-soft-success' : 'badge-soft-secondary'}`}
                                                style={{ fontSize: '0.75rem' }}
                                            >
                                                {plan.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <div className="d-flex align-items-center justify-content-center gap-1">
                                                <ButtonTableAction
                                                    callbackFunction={() => handleEdit(plan.id)}
                                                    title="Editar"
                                                    classIcon="fa-edit"
                                                    colorIcon="text-warning"
                                                    sizeIcon="16"
                                                />
                                                <ButtonTableAction
                                                    callbackFunction={() => handleToggleActive(plan)}
                                                    title={plan.is_active ? 'Desactivar' : 'Activar'}
                                                    classIcon={plan.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}
                                                    colorIcon={plan.is_active ? 'text-success' : 'text-secondary'}
                                                    sizeIcon="16"
                                                />
                                                <ButtonTableAction
                                                    callbackFunction={() => handleDelete(plan)}
                                                    title="Eliminar"
                                                    classIcon="fa-trash"
                                                    colorIcon="text-danger"
                                                    sizeIcon="16"
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Información */}
                <div className="mt-3">
                    <small className="text-muted">
                        Mostrando {filteredPlans.length} de {plans.length} plan(es)
                    </small>
                </div>

                {/* Nota informativa */}
                <div className="mt-4 p-3 rounded" style={{ background: '#f0f4ff', border: '1px solid #001849' }}>
                    <h6 className="mb-2" style={{ color: '#001849' }}>
                        <i className="fas fa-info-circle me-2"></i>
                        Funcionamiento del Sistema de Membresías
                    </h6>
                    <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                        <li><strong>Con membresía activa:</strong> Contratos gratis y acceso al chatbot legal</li>
                        <li><strong>Sin membresía:</strong> Debe pagar cada contrato individualmente, chatbot bloqueado</li>
                        <li><strong>Contratos = -1:</strong> Significa contratos ilimitados</li>
                        <li><strong>Chatbot msgs = -1:</strong> Significa mensajes ilimitados</li>
                    </ul>
                </div>
            </DefaultCard>
        </div>
    );
};

export default MembershipPlanList;
